import { useCallback, useContext, useMemo, useState } from "react";
import { getImporterReconciliationQueue } from "@common/helpers/financialDocument";
import { ReconciliationSummaryResponse } from "@common/contracts/reconciliation.contract";
import { pluralizeWithCount } from "@common/helpers/string";
import {
    useHasOutstandingCashItems,
    useIsReconciliationSaving,
} from "@api/reconciliation.api";
import {
    ReconciliationDrawer,
    ReconciliationDrawerStep,
} from "./ReconciliationDrawer";
import { ImporterContextProvider } from "../importer/ImporterContextProvider";
import { Importer } from "../importer/Importer";
import { ReconciliationMatchStep } from "./ReconciliationMatchStep";
import { ReconciliationContextProvider } from "./ReconciliationContextProvider";
import { ReconciliationContext } from "./Reconciliation.context";
import { ReconciliationClearStep } from "./ReconciliationClearStep";
import { useReconciliationSummaryLoader } from "./useReconciliationSummaryLoader";
import { dropStaleCheckMerges } from "./pairMatching";
import {
    countSuggestedActions,
    getLastReconciledLabel,
    getReconciliationTitle,
    ReconciliationReviewMode,
    ReconciliationShellProps,
} from "./helpers";

enum ReconciliationWorkflowStep {
    Import = "import",
    Review = "review",
    Match = "match",
    Clear = "clear",
}

const STEP_ACTIONS: Record<ReconciliationWorkflowStep, string> = {
    [ReconciliationWorkflowStep.Import]: "Import statements",
    [ReconciliationWorkflowStep.Review]: "Review",
    [ReconciliationWorkflowStep.Match]: "Match bank transactions",
    [ReconciliationWorkflowStep.Clear]: "Clear outstanding checks and deposits",
};

const STEP_NUMBERS: Record<ReconciliationWorkflowStep, number> = {
    [ReconciliationWorkflowStep.Import]: 1,
    [ReconciliationWorkflowStep.Review]: 2,
    [ReconciliationWorkflowStep.Match]: 2,
    [ReconciliationWorkflowStep.Clear]: 3,
};

const ReconciliationWorkflowSteps: React.FC<ReconciliationShellProps> = ({
    show,
    reconciliedFinancialAccount,
    previousReconciliation,
    isLoadingLastReconciliation,
    onHide,
    onSuccess,
}) => {
    const {
        statementTransactionsToAddIdsSet,
        financialTransactionsToRemoveIdsSet,
        checkMerges,
        replaceCheckMerges,
    } = useContext(ReconciliationContext);
    const [step, setStep] = useState<ReconciliationWorkflowStep>(
        ReconciliationWorkflowStep.Import,
    );
    const [summary, setSummary] = useState<ReconciliationSummaryResponse>();
    const isSaving = useIsReconciliationSaving();
    const loadSummary = useReconciliationSummaryLoader(
        reconciliedFinancialAccount,
    );
    const { data: hasOutstandingCashItemsBeforeImport = false } =
        useHasOutstandingCashItems({
            financialAccount: reconciliedFinancialAccount,
            enabled: show,
        });
    const hasOutstandingCashItems = summary
        ? summary.outstandingCashItems.length > 0
        : hasOutstandingCashItemsBeforeImport;
    const hasClearStep =
        step === ReconciliationWorkflowStep.Clear || hasOutstandingCashItems;

    const goToTransactionsReview = useCallback(async () => {
        const loadedSummary = await loadSummary();
        if (!loadedSummary) {
            return;
        }

        setSummary(loadedSummary);
        setStep(
            loadedSummary.outstandingCashItems.length > 0
                ? ReconciliationWorkflowStep.Match
                : ReconciliationWorkflowStep.Review,
        );
    }, [loadSummary]);

    const goToClearStep = useCallback(() => {
        replaceCheckMerges(
            dropStaleCheckMerges({
                pairs: summary?.transactionsPairs ?? [],
                checkMerges,
                statementTransactionsToAddIdsSet,
                financialTransactionsToRemoveIdsSet,
            }),
        );
        setStep(ReconciliationWorkflowStep.Clear);
    }, [
        summary,
        checkMerges,
        statementTransactionsToAddIdsSet,
        financialTransactionsToRemoveIdsSet,
        replaceCheckMerges,
    ]);

    const reviewMode = useMemo<ReconciliationReviewMode>(
        () =>
            step === ReconciliationWorkflowStep.Match
                ? { kind: "workflow-match", onContinue: goToClearStep }
                : { kind: "workflow-review" },
        [step, goToClearStep],
    );

    const isReviewing =
        step === ReconciliationWorkflowStep.Review ||
        step === ReconciliationWorkflowStep.Match;
    const workflowStep: ReconciliationDrawerStep = {
        number: STEP_NUMBERS[step],
        total: hasClearStep ? 3 : 2,
        action: STEP_ACTIONS[step],
        details:
            isReviewing && summary
                ? [
                      pluralizeWithCount(
                          "suggested action",
                          countSuggestedActions(summary.transactionsPairs),
                      ),
                  ]
                : undefined,
    };

    return (
        <ReconciliationDrawer
            open={show}
            onClose={onHide}
            title={getReconciliationTitle(reconciliedFinancialAccount)}
            closeLabel="Close reconciliation"
            subtitle={getLastReconciledLabel({
                previousReconciliation,
                isLoadingLastReconciliation,
            })}
            step={workflowStep}
            isBusy={isSaving}
        >
            {step === ReconciliationWorkflowStep.Import && (
                <Importer
                    onHide={onHide}
                    onNext={goToTransactionsReview}
                    layout="drawer"
                />
            )}
            {isReviewing && summary && (
                <ReconciliationMatchStep
                    mode={reviewMode}
                    financialAccount={reconciliedFinancialAccount}
                    previousReconciliation={previousReconciliation}
                    onBack={() => setStep(ReconciliationWorkflowStep.Import)}
                    onHide={onHide}
                    onSuccess={onSuccess}
                    onOpeningBalanceUpdated={goToTransactionsReview}
                    summary={summary}
                    setSummary={setSummary}
                />
            )}
            {step === ReconciliationWorkflowStep.Clear && summary && (
                <ReconciliationClearStep
                    financialAccount={reconciliedFinancialAccount}
                    summary={summary}
                    previousClosingDate={previousReconciliation?.closingDate}
                    onBack={() => setStep(ReconciliationWorkflowStep.Match)}
                    onHide={onHide}
                    onSuccess={onSuccess}
                />
            )}
        </ReconciliationDrawer>
    );
};

export const ReconciliationWorkflow: React.FC<ReconciliationShellProps> = (
    props,
) => (
    <ImporterContextProvider
        importerQueue={getImporterReconciliationQueue(
            props.reconciliedFinancialAccount.id,
        )}
        reconciliedFinancialAccount={props.reconciliedFinancialAccount}
        readyToImportLabel="Ready to reconcile"
    >
        <ReconciliationContextProvider>
            <ReconciliationWorkflowSteps {...props} />
        </ReconciliationContextProvider>
    </ImporterContextProvider>
);
